/**
 * The server's operation catalog (GET /api/v1/ops) and the contact screen's billing, automation and
 * event reads. Every screen uses the real operations; there is no sample data any more (2026-11-04).
 */
import { api, ApiError, qs } from "./api";

/** True when the server has the operation (GET /api/v1/ops lists them). Used only for optional
 *  features; a failed catalog call is never cached, so the next call asks again. */
interface OpInfo {
  name: string;
  method?: string;
  path?: string;
}
let opsCache: Promise<Map<string, OpInfo>> | null = null;
function ops(): Promise<Map<string, OpInfo>> {
  if (!opsCache) {
    const p = api<{ ops?: OpInfo[]; operations?: OpInfo[] }>("/api/v1/ops").then((r) => new Map((r.ops ?? r.operations ?? []).map((o) => [o.name, o])));
    opsCache = p;
    // Don't remember a failure (signed out, a deploy in progress, a network blip): the next call retries.
    p.catch(() => {
      if (opsCache === p) opsCache = null;
    });
  }
  return opsCache;
}

/** Forget the catalog (after signing in or out), so the next hasOp/callOp asks the server again. */
export function resetOpsCache() {
  opsCache = null;
}

export function hasOp(name: string): Promise<boolean> {
  return ops().then(
    (m) => m.has(name),
    () => false,
  );
}

/** Calls an operation by name with its registered method and path, for ops whose REST path another stream picks:
 *  {param} path parts come from input; the rest goes in the query (GET) or the JSON body. */
export async function callOp<T>(name: string, input: Record<string, string | number | boolean | undefined> = {}): Promise<T> {
  const op = (await ops()).get(name);
  if (!op?.path) throw new ApiError(404, "not_found", `The server has no ${name} operation yet.`);
  const rest = { ...input };
  const path = op.path.replace(/\{([a-z_]+)\}/g, (_, k: string) => {
    const v = rest[k] ?? (k === "id" ? (rest.product_id ?? rest.contact_id) : undefined);
    delete rest[k];
    if (k === "id") delete rest.product_id;
    return encodeURIComponent(String(v ?? ""));
  });
  const method = (op.method ?? "GET").toUpperCase();
  if (method === "GET") return api<T>(path + qs(rest));
  return api<T>(path, { method, json: rest });
}

// ---------------------------------------------------------------------------------------------
// Billing (crm-billing): billing_contact_summary (GET /api/v1/contacts/{id}/billing) and
// billing_contact_detail (GET /api/v1/contacts/{id}/billing/detail), merged 2026-10-26. Shapes from
// internal/billing/contact_billing.go.

export interface BillingCard {
  id?: number;
  brand: string;
  last4: string;
  exp: string;
  gateway: string;
  default?: boolean;
  /** active, expired, failed, removed */
  status?: string;
  /** Stripe wallet (apple_pay, google_pay, link, amazon_pay, cashapp); Link, Amazon Pay and Cash App Pay have no last 4. */
  wallet?: string | null;
  /** The customer in Stripe's dashboard (test or live), built by billing. */
  dashboard_url?: string | null;
}

export interface BillingSummary {
  next_charge: { date: string; amount_cents: number; plan: string } | null;
  card: BillingCard | null;
  open_invoices: number;
  overdue_cents: number;
  /** Always null (go-live audit XC-H1): the link is made on click with billing_card_update_link. */
  update_card_url?: string | null;
  /** Whether "copy update-card link" applies (a subscription, a card or an open invoice). */
  card_link_available?: boolean;
  /** The brand whose look the update-card page uses. */
  card_link_brand_id?: number;
  pending_approvals?: number;
  ltv_cents?: number;
  /** Chargebacks still open on their payments (billing records Stripe's charge.dispute.* events). */
  open_disputes?: number;
  /** Their customer page in Stripe's dashboard. */
  stripe_customer_url?: string | null;
}

export interface BillingDetail {
  subscriptions: {
    id: number;
    plan: string;
    status: string;
    amount_cents: number;
    interval: string;
    next_charge_on: string | null;
    started_on: string;
    ends_on?: string | null;
    /** false = the CRM doesn't charge it */
    billing_enabled?: boolean;
    /** crm, keap (Keap charges it) or moving */
    billed_by?: string;
    /** a Club membership (Keap bills it until Brandon moves the member) */
    club?: boolean;
  }[];
  orders: { id: number; placed_at: string; description: string; total_cents: number; status: string; brand_id: number; source?: string }[];
  invoices: { id?: number; number: string; due_on: string | null; total_cents: number; balance_cents: number; status: string; description?: string; pay_url?: string }[];
  payments: {
    id: number;
    at: string;
    amount_cents: number;
    refunded_cents?: number;
    /** billing's own label ("Visa ending 4242"); the parts below, when sent, are used instead. */
    method: string;
    status: string;
    message?: string;
    invoice_id?: number;
    gateway?: string;
    card_brand?: string | null;
    last4?: string | null;
    wallet?: string | null;
    dashboard_url?: string | null;
    dispute_status?: string | null;
    disputed_at?: string | null;
    dispute_reason?: string | null;
    /** Why it was charged, in one plain sentence (billing_charge_explain). */
    sentence?: string;
  }[];
  cards: BillingCard[];
  stripe_customer_url?: string | null;
  access?: { key: string; until?: string | null; sources: string[] }[];
}

export function getBillingSummary(contactId: number) {
  return api<BillingSummary>(`/api/v1/contacts/${contactId}/billing`);
}

export function getBillingDetail(contactId: number) {
  return api<BillingDetail>(`/api/v1/contacts/${contactId}/billing/detail`);
}

// ---------------------------------------------------------------------------------------------
// Automations (crm-automations): automations_contact_positions (GET /api/v1/contacts/{id}/automations),
// merged 2026-10-26.

export interface AutomationPosition {
  enrollment_id: number;
  automation_id: number;
  automation_name: string;
  automation_status: string;
  version: number;
  status: "active" | "waiting" | "paused" | "completed" | "exited" | string;
  shadow: boolean;
  step_label: string;
  step_number: number;
  step_count: number;
  summary: string;
  next_run_at: string | null;
  entered_at: string;
  completed_at: string | null;
  exit_reason: string | null;
  /** Later steps this run will pass over (marked by staff with "Skip or move"). */
  skipping?: { step_key: string; step_label: string; subject?: string; by: string; at: string }[];
}

export function getAutomationPositions(contactId: number) {
  return api<{ positions: AutomationPosition[] }>(`/api/v1/contacts/${contactId}/automations`).then((r) => r.positions ?? []);
}

// ---------------------------------------------------------------------------------------------
// Events (crm-automations): events_contact_upcoming (GET /api/v1/contacts/{id}/events), merged 2026-10-26.

export interface UpcomingEvent {
  registration_id: number;
  event_id: number;
  event: string;
  kind: string;
  brand_id: number;
  session_id: number | null;
  starts_at: string | null;
  ends_at: string | null;
  when: string;
  location: string;
  status: "registered" | "waitlisted" | "attended" | "no_show" | "cancelled" | string;
  guests: number;
  join_url: string | null;
  checked_in_at: string | null;
}

export function getUpcomingEvents(contactId: number) {
  return api<{ registrations: UpcomingEvent[] }>(`/api/v1/contacts/${contactId}/events`).then((r) => r.registrations ?? []);
}
